import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NgClass, NgFor, NgIf, SlicePipe } from '@angular/common';
import { HeroSectionService } from './hero-section.service';

@Component({
  selector: 'app-hero-section',
  standalone: true,
  imports: [NgClass, NgFor, NgIf, SlicePipe],
  templateUrl: './hero-section.component.html',
  styleUrl: './hero-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeroSectionComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('heroVideo') private readonly heroVideo?: ElementRef<HTMLVideoElement>;

  get content() {
    return this.heroService.content;
  }

  get heroSlides() {
    return this.heroService.slides;
  }

  currentIndex = 0;

  private autoplayId?: number;
  private isPaused = false;
  private isManualPause = false;
  private touchStartX = 0;
  private reduceMotion = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly heroService: HeroSectionService
  ) {}

  ngOnInit(): void {
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.reduceMotion) {
      this.startAutoplay();
    }
  }

  ngAfterViewInit(): void {
    const video = this.heroVideo?.nativeElement;
    if (!video) {
      return;
    }

    video.muted = true;
    video.playsInline = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay may be blocked; ignore to avoid console noise.
      });
    }
  }

  ngOnDestroy(): void {
    this.clearAutoplay();
  }

  next(): void {
    this.pause(true);
    if (this.heroSlides.length === 0) {
      return;
    }
    this.currentIndex = (this.currentIndex + 1) % this.heroSlides.length;
    this.cdr.markForCheck();
  }

  prev(): void {
    this.pause(true);
    if (this.heroSlides.length === 0) {
      return;
    }
    this.currentIndex = (this.currentIndex - 1 + this.heroSlides.length) % this.heroSlides.length;
    this.cdr.markForCheck();
  }

  goTo(index: number): void {
    this.pause(true);
    if (index < 0 || index >= this.heroSlides.length) {
      return;
    }
    this.currentIndex = index;
    this.cdr.markForCheck();
  }

  pause(manual = false): void {
    this.isPaused = true;
    if (manual) {
      this.isManualPause = true;
    }
  }

  resume(): void {
    if (this.isManualPause) {
      return;
    }
    this.isPaused = false;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0]?.clientX ?? 0;
  }

  onTouchEnd(event: TouchEvent): void {
    const touchEndX = event.changedTouches[0]?.clientX ?? 0;
    const delta = touchEndX - this.touchStartX;
    if (Math.abs(delta) < 40) {
      return;
    }
    if (delta > 0) {
      this.prev();
    } else {
      this.next();
    }
  }

  private startAutoplay(): void {
    if (this.heroSlides.length < 2) {
      return;
    }
    this.autoplayId = window.setInterval(() => {
      if (this.isPaused) {
        return;
      }
      this.currentIndex = (this.currentIndex + 1) % this.heroSlides.length;
      this.cdr.markForCheck();
    }, 3000);
  }

  private clearAutoplay(): void {
    if (this.autoplayId) {
      window.clearInterval(this.autoplayId);
      this.autoplayId = undefined;
    }
  }
}
